import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiX, FiLink, FiType, FiBox, FiServer, FiGlobe, FiFile, FiPlus,
    FiUpload, FiDownload, FiAlertCircle, FiArrowRight, FiCode, FiLayout,
    FiCheckCircle, FiAlertTriangle, FiInfo, FiFileText
} from "react-icons/fi";
import axios from "../../../utils/api/axios";
import { toast } from "react-hot-toast";

interface BulkImportModalProps {
    assetId: string;
    assetName: string;
    targetId: string;
    targetName: string;
    assetCategory?: string;
    onClose: () => void;
    onSuccess: () => void;
}

/**
 * Resource type descriptor used in the UI.
 * `backendType`  → what gets sent to the API as `resource_type`
 * `defaultMethod`→ if set, HTTP method is locked to this (no picker shown)
 */
interface ResourceTypeDef {
    id: string;           // internal UI id
    backendType: string;  // value sent to backend
    label: string;
    sublabel: string;     // one-line clarification shown under the tile
    icon: React.ReactNode;
    defaultMethod?: string; // if defined, method is locked and not shown
    showMethodPicker?: boolean;
    placeholder: string;
    hint: string;         // helper text shown under the value input
    categories: string[];
}

const ALL_RESOURCE_TYPES: ResourceTypeDef[] = [
    {
        id: "PAGE",
        backendType: "PATH",
        label: "Web Page",
        sublabel: "Static or dynamic page URL",
        icon: <FiLayout />,
        defaultMethod: "GET",
        showMethodPicker: false,
        placeholder: "/about  or  /dashboard/settings",
        hint: "Web pages are always accessed via GET. No method selection needed.",
        categories: ["Digital Applications"],
    },
    {
        id: "API",
        backendType: "PATH",
        label: "API Endpoint",
        sublabel: "REST / GraphQL route",
        icon: <FiCode />,
        showMethodPicker: true,
        placeholder: "/api/v1/users  or  /graphql",
        hint: "Select the HTTP method that applies to this route.",
        categories: ["Digital Applications"],
    },
    {
        id: "IP",
        backendType: "IP",
        label: "IP Address",
        sublabel: "IPv4 host or subnet",
        icon: <FiGlobe />,
        showMethodPicker: false,
        placeholder: "192.168.1.1",
        hint: "Enter a valid IPv4 address.",
        categories: ["Infrastructure", "Integrations & Devices", "Identity & Data"],
    },
    {
        id: "PORT",
        backendType: "PORT",
        label: "Port",
        sublabel: "Network service port",
        icon: <FiServer />,
        showMethodPicker: false,
        placeholder: "8443",
        hint: "Enter a port number between 1 and 65535.",
        categories: ["Infrastructure", "Integrations & Devices"],
    },
    {
        id: "PARAMETER",
        backendType: "PARAMETER",
        label: "Parameter",
        sublabel: "Query / body param",
        icon: <FiType />,
        showMethodPicker: false,
        placeholder: "user_id  or  ?search=",
        hint: "A request parameter to include in scope.",
        categories: ["Digital Applications"],
    },
    {
        id: "HEADER",
        backendType: "HEADER",
        label: "Header",
        sublabel: "HTTP request header",
        icon: <FiBox />,
        showMethodPicker: false,
        placeholder: "Authorization  or  X-API-Key",
        hint: "An HTTP header to be evaluated during testing.",
        categories: ["Digital Applications"],
    },
    {
        id: "FILE",
        backendType: "FILE",
        label: "File",
        sublabel: "Uploadable file or asset",
        icon: <FiFile />,
        showMethodPicker: false,
        placeholder: "/uploads/report.pdf",
        hint: "A file path or upload endpoint to include in scope.",
        categories: ["Digital Applications", "Code & Supply Chain", "Identity & Data"],
    },
    {
        id: "OTHER",
        backendType: "OTHER",
        label: "Other",
        sublabel: "Anything not listed above",
        icon: <FiPlus />,
        showMethodPicker: false,
        placeholder: "Describe the endpoint…",
        hint: "Use this for custom resource types not covered above.",
        categories: ["Digital Applications", "Infrastructure", "Identity & Data", "Code & Supply Chain", "Integrations & Devices"],
    },
];

const BulkImportModal: React.FC<BulkImportModalProps> = ({
    assetId, assetName, targetId, targetName, assetCategory, onClose, onSuccess
}) => {
    const [mode, setMode] = useState<"single" | "bulk_text" | "file">("single");
    const [value, setValue] = useState("");
    const [bulkText, setBulkText] = useState("");
    const [httpMethod, setHttpMethod] = useState("GET");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showImportGuide, setShowImportGuide] = useState(false);

    const filteredResourceTypes = useMemo(() => {
        if (!assetCategory) return ALL_RESOURCE_TYPES;
        const filtered = ALL_RESOURCE_TYPES.filter(t => t.categories.includes(assetCategory));
        return filtered.length > 0 ? filtered : ALL_RESOURCE_TYPES;
    }, [assetCategory]);

    const [resourceTypeId, setResourceTypeId] = useState<string>(filteredResourceTypes[0].id);

    const selectedType = filteredResourceTypes.find(t => t.id === resourceTypeId) ?? filteredResourceTypes[0];

    const validateValue = (val: string, typeDef: ResourceTypeDef): string | null => {
        if (!val) return null;
        switch (typeDef.backendType) {
            case "IP": {
                const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                return ipRegex.test(val) ? null : "Invalid IPv4 address format";
            }
            case "PORT": {
                const port = parseInt(val);
                return (!isNaN(port) && port >= 1 && port <= 65535 && /^\d+$/.test(val))
                    ? null : "Port must be a number between 1 and 65535";
            }
            case "PATH":
                return val.startsWith("/") ? null : "Path should start with '/'";
            default:
                return null;
        }
    };

    const resolvedMethod = (): string | null => {
        if (selectedType.defaultMethod) return selectedType.defaultMethod;
        if (selectedType.showMethodPicker) return httpMethod;
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (mode === "single") {
                await axios.post(`/assets/${assetId}/targets/${targetId}/resources`, {
                    value,
                    resource_type: selectedType.backendType,
                    http_method: resolvedMethod(),
                    notes,
                    status: "IN_SCOPE",
                });
                toast.success("Endpoint added successfully");
            } else if (mode === "bulk_text") {
                const values = bulkText.split(/[\n,]/).map(v => v.trim()).filter(v => v !== "");
                if (values.length === 0) {
                    toast.error("Please enter at least one value");
                    setLoading(false);
                    return;
                }
                const resources = values.map(v => ({
                    value: v,
                    resource_type: selectedType.backendType,
                    http_method: resolvedMethod(),
                    notes: "Bulk text import",
                }));
                await axios.post(`/assets/${assetId}/targets/${targetId}/resources/bulk`, { resources });
                toast.success(`Imported ${resources.length} endpoints`);
            }
            onSuccess();
        } catch (error: any) {
            console.error("Error adding resource:", error);
            toast.error(error.response?.data?.message || "Operation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLoading(true);
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const content = event.target?.result as string;
                let resources: any[] = [];
                if (file.name.endsWith(".json")) {
                    const data = JSON.parse(content);
                    resources = Array.isArray(data) ? data : [data];
                } else if (file.name.endsWith(".csv")) {
                    const lines = content.split("\n");
                    const headers = lines[0].split(",").map(h => h.trim());
                    resources = lines.slice(1).filter(l => l.trim() !== "").map(line => {
                        const vals = line.split(",").map(v => v.trim());
                        const resource: any = {};
                        headers.forEach((h, i) => { resource[h.toLowerCase().replace(" ", "_")] = vals[i]; });
                        return resource;
                    });
                }
                const mappedResources = resources.map(r => {
                    const rawType = (r.resource_type || r.type || "PATH").trim().toUpperCase();
                    const rawMethod = (r.http_method || r.method || "").trim().toUpperCase();

                    // Normalization Maps (Case-Insensitive search)
                    const validMethods = ["GET", "POST", "PUT", "DELETE", "PATCH"];
                    const normalizedMethod = validMethods.find(m => m === rawMethod) || (rawMethod ? rawMethod : null);

                    const typeMap: Record<string, string> = {
                        "WEB PAGE": "PATH",
                        "API ENDPOINT": "PATH",
                        "IP ADDRESS": "IP",
                        "PORT": "PORT",
                        "PARAMETER": "PARAMETER",
                        "HEADER": "HEADER",
                        "FILE": "FILE",
                        "OTHER": "OTHER"
                    };
                    const normalizedType = typeMap[rawType] || rawType;

                    return {
                        value: r.value || r.endpoint || r.path,
                        resource_type: normalizedType,
                        http_method: normalizedMethod,
                        notes: r.notes || "Imported from file",
                    };
                }).filter(r => r.value);
                if (mappedResources.length === 0) throw new Error("No valid endpoints found in file");
                await axios.post(`/assets/${assetId}/targets/${targetId}/resources/bulk`, { resources: mappedResources });
                toast.success(`File import successful: ${mappedResources.length} items`);
                onSuccess();
            } catch (error: any) {
                console.error("File processing error:", error);
                toast.error(error.message || "Failed to process file");
            } finally {
                setLoading(false);
                e.target.value = "";
            }
        };
        reader.readAsText(file);
    };

    const downloadTemplate = (format: "csv" | "json") => {
        const content = format === "json"
            ? JSON.stringify([
                { value: "/api/v1/users", resource_type: "PATH", http_method: "GET", notes: "User list API" },
                { value: "/about", resource_type: "PATH", http_method: "GET", notes: "Static about page" },
            ], null, 2)
            : "value,resource_type,http_method,notes\n/api/v1/auth,PATH,POST,Authentication endpoint\n/about,PATH,GET,Static page\n192.168.1.1,IP,,Internal server";
        const blob = new Blob([content], { type: format === "json" ? "application/json" : "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `endpoint_template.${format}`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-start justify-end p-4 sm:p-6 bg-[#0B0F19]/60 backdrop-blur-sm">
            <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 50, opacity: 0 }}
                className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-48px)] mt-8 border border-slate-200"
            >
                {/* Header */}
                <div className="bg-white px-6 py-4 relative flex justify-between items-center border-b border-slate-100">
                    <div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-1.5 min-w-0">
                            <div className="flex items-center gap-2 text-[10px] font-medium text-slate-500 shrink-0">
                                <span>Asset</span>
                                <FiArrowRight className="w-2.5 h-2.5 text-slate-400" />
                                <span className="text-slate-900 font-semibold">{assetName}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] font-medium text-slate-500 shrink-0">
                                <span>Target</span>
                                <FiArrowRight className="w-2.5 h-2.5 text-slate-400" />
                                <span className="text-slate-900 font-semibold">{targetName}</span>
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-[#0A192F]">Provision Endpoints</h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 hover:bg-slate-50 rounded-lg">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Mode Toggle */}
                <div className="bg-[#0A192F] px-6 py-4 border-b border-slate-800">
                    <div className="flex bg-white/5 p-1 rounded-xl">
                        {([
                            { id: "single", label: "Single" },
                            { id: "bulk_text", label: "Bulk" },
                            { id: "file", label: "File" },
                        ] as { id: "single" | "bulk_text" | "file"; label: string }[]).map(m => (
                            <button
                                key={m.id}
                                onClick={() => setMode(m.id)}
                                className={`flex-1 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${mode === m.id ? "bg-[#65D01E] text-white shadow-lg shadow-[#65D01E]/20" : "text-slate-400 hover:text-slate-300"}`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                    <form onSubmit={handleSubmit} className="space-y-5">

                        {/* Resource Type Selector */}
                        {mode !== "file" && (
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    What are you adding?
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {filteredResourceTypes.map(t => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => {
                                                setResourceTypeId(t.id);
                                                setValue("");
                                                setValidationError(null);
                                                if (t.defaultMethod) setHttpMethod(t.defaultMethod);
                                            }}
                                            className={`flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all text-center gap-1 ${resourceTypeId === t.id
                                                ? "border-[#65D01E] bg-[#f0f9eb] text-[#22863a] shadow-sm"
                                                : "border-slate-100 text-slate-400 hover:border-slate-200 hover:bg-slate-50"
                                            }`}
                                        >
                                            <span className="text-base">{t.icon}</span>
                                            <span className="text-[9px] font-black uppercase leading-tight">{t.label}</span>
                                            <span className="text-[8px] font-medium text-slate-400 leading-tight">{t.sublabel}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Single Mode */}
                        {mode === "single" && (
                            <div className="space-y-4">
                                {/* Value Input */}
                                <div className="relative">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                        {selectedType.label} Value
                                    </label>
                                    <input
                                        type="text"
                                        value={value}
                                        onChange={e => {
                                            setValue(e.target.value);
                                            setValidationError(validateValue(e.target.value, selectedType));
                                        }}
                                        placeholder={selectedType.placeholder}
                                        className={`w-full px-4 py-2.5 bg-slate-50 border rounded-xl text-sm font-bold focus:ring-4 outline-none transition-all ${validationError ? "border-red-400 focus:ring-red-500/10 focus:border-red-500" : "border-slate-100 focus:ring-[#65D01E]/10 focus:border-[#65D01E]"}`}
                                        required
                                    />
                                    {validationError ? (
                                        <p className="text-[10px] text-red-500 font-bold mt-1 ml-1">{validationError}</p>
                                    ) : (
                                        <p className="text-[10px] text-slate-400 mt-1 ml-1">{selectedType.hint}</p>
                                    )}
                                </div>

                                {/* HTTP Method — only for API endpoints */}
                                {selectedType.showMethodPicker && (
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                            HTTP Method
                                        </label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {["GET", "POST", "PUT", "DELETE", "PATCH"].map(m => (
                                                <button
                                                    key={m}
                                                    type="button"
                                                    onClick={() => setHttpMethod(m)}
                                                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black border-2 transition-all ${httpMethod === m
                                                        ? "border-[#65D01E] bg-[#f0f9eb] text-[#22863a] shadow-sm"
                                                        : "border-slate-100 text-slate-400 hover:border-slate-200"
                                                    }`}
                                                >
                                                    {m}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Auto-method badge for pages */}
                                {selectedType.defaultMethod && (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl">
                                        <span className="px-2 py-0.5 bg-[#f0f9eb] text-[#22863a] border border-[#65D01E]/30 rounded text-[10px] font-black uppercase">
                                            {selectedType.defaultMethod}
                                        </span>
                                        <span className="text-[10px] text-slate-500 font-medium">
                                            Method auto-assigned — web pages are always GET
                                        </span>
                                    </div>
                                )}

                                {/* Notes */}
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                        Notes <span className="font-medium normal-case tracking-normal text-slate-300">(optional)</span>
                                    </label>
                                    <textarea
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        placeholder="Brief notes for the security assessment..."
                                        rows={2}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-xs font-medium focus:ring-4 focus:ring-[#65D01E]/10 focus:border-[#65D01E] outline-none transition-all resize-none"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Bulk Text Mode */}
                        {mode === "bulk_text" && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                        Paste values — one per line or comma-separated
                                    </label>
                                    <textarea
                                        value={bulkText}
                                        onChange={e => setBulkText(e.target.value)}
                                        placeholder={selectedType.placeholder}
                                        rows={5}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl text-xs font-mono focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-none"
                                        required
                                    />
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-amber-50/50 rounded-xl border border-amber-100/50">
                                    <FiAlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                                    <p className="text-[10px] font-bold text-amber-700 leading-relaxed">
                                        All entries will be tagged as <span className="font-black">{selectedType.label}</span>
                                        {selectedType.defaultMethod && (
                                            <> with method <span className="font-black">{selectedType.defaultMethod}</span></>
                                        )}
                                        {selectedType.showMethodPicker && (
                                            <> with method <span className="font-black">{httpMethod}</span></>
                                        )}.
                                        {" "}Change the type above if needed before importing.
                                    </p>
                                </div>
                                {/* Method picker in bulk mode if API selected */}
                                {selectedType.showMethodPicker && (
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">
                                            HTTP Method (applied to all)
                                        </label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {["GET", "POST", "PUT", "DELETE", "PATCH"].map(m => (
                                                <button
                                                    key={m}
                                                    type="button"
                                                    onClick={() => setHttpMethod(m)}
                                                    className={`px-3 py-1.5 rounded-lg text-[10px] font-black border-2 transition-all ${httpMethod === m
                                                        ? "border-[#65D01E] bg-[#f0f9eb] text-[#22863a] shadow-sm"
                                                        : "border-slate-100 text-slate-400 hover:border-slate-200"
                                                    }`}
                                                >
                                                    {m}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* File Mode */}
                        {mode === "file" && (
                            <div className="space-y-5">
                                <div className="relative group">
                                    <input
                                        type="file"
                                        onChange={handleFileUpload}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                        accept=".csv,.json"
                                    />
                                    <div className="border-2 border-dashed border-slate-100 rounded-2xl p-8 text-center transition-all hover:bg-slate-50 group-hover:border-[#65D01E]/30">
                                        <div className="w-12 h-12 bg-slate-100 text-[#65D01E] rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all group-hover:scale-110">
                                            <FiUpload className="w-6 h-6" />
                                        </div>
                                        <h4 className="text-sm font-bold text-slate-900 tracking-tight">Drop or click to upload</h4>
                                        <p className="text-slate-400 text-[9px] font-bold uppercase tracking-widest mt-1">JSON / CSV (Max 5MB)</p>
                                    </div>
                                </div>
                                <div className="p-3 bg-slate-50 border border-slate-100 rounded-xl text-[10px] text-slate-500 leading-relaxed">
                                    <span className="font-black text-slate-700">CSV columns:</span> <code>value, resource_type, http_method, notes</code><br />
                                    Use <code>PATH</code> as <code>resource_type</code> for both web pages and API routes. Set <code>http_method</code> to <code>GET</code> for pages.
                                </div>
                                <div className="flex items-center justify-center gap-3">
                                    <button type="button" onClick={() => downloadTemplate("csv")}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                                        <FiDownload /> CSV Template
                                    </button>
                                    <button type="button" onClick={() => downloadTemplate("json")}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                                        <FiDownload /> JSON Template
                                    </button>
                                    <button type="button" onClick={() => setShowImportGuide(true)}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all">
                                        <FiFileText /> View Guide
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Submit Buttons */}
                        {mode !== "file" && (
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-3 bg-slate-50 hover:bg-slate-100 text-slate-400 rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || !!validationError}
                                    className="flex-[2] py-3 bg-[#65D01E] hover:bg-[#58b51a] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-[10px] uppercase tracking-widest transition-all shadow-lg shadow-[#65D01E]/10"
                                >
                                    {loading ? "Processing…" : mode === "single" ? "Provision" : "Batch Provision"}
                                </button>
                            </div>
                        )}
                    </form>
                </div>
            </motion.div>

            {/* ==================== IMPORT GUIDE MODAL ==================== */}
            <AnimatePresence>
                {showImportGuide && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setShowImportGuide(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#65D01E] flex items-center justify-center text-white shadow-lg shadow-green-200">
                                        <FiFileText className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-black text-slate-900 tracking-tight">Endpoint Import Guide</h2>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Case-Insensitive Normalization</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowImportGuide(false)}
                                    className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-900"
                                >
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                                {/* Success Icon Row */}
                                <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-3">
                                    <FiInfo className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <p className="font-bold text-blue-900 mb-1">User Friendly Rule: Case-Insensitivity</p>
                                        <p className="text-blue-800 leading-relaxed">Values like <code className="bg-blue-100 px-1 rounded">get</code>, <code className="bg-blue-100 px-1 rounded">path</code>, or <code className="bg-blue-100 px-1 rounded">IP</code> are now automatically normalized to the correct format during import. Standard capitalization is still recommended.</p>
                                    </div>
                                </div>

                                {/* Mapping Tables */}
                                <div className="space-y-6">
                                    {/* Table Style Wrapper */}
                                    <style dangerouslySetInnerHTML={{ __html: `
                                        .guide-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; }
                                        .guide-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-weight: 800; color: #64748b; border-bottom: 2px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.05em; }
                                        .guide-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; color: #1e293b; font-weight: 500; }
                                        .guide-table tr:hover td { background: #f8fafc; }
                                        .type-badge { background: #1e293b; color: white; padding: 8px 12px; font-size: 10px; font-weight: 900; border-radius: 6px; display: inline-block; margin-bottom: 10px; }
                                    `}} />

                                    <div>
                                        <div className="type-badge tracking-widest uppercase">CSV/JSON `resource_type` Specification</div>
                                        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                            <table className="guide-table">
                                                <thead>
                                                    <tr>
                                                        <th>`resource_type` (Value in File)</th>
                                                        <th>Description / Purpose</th>
                                                        <th>Sample Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr><td className="font-bold underline">PATH</td><td>Standard Web Pages or API Routes</td><td>/dashboard, /api/v1/auth</td></tr>
                                                    <tr><td className="font-bold underline">IP</td><td>Server IP addresses (IPv4)</td><td>192.168.1.1, 10.0.0.5</td></tr>
                                                    <tr><td className="font-bold underline">PORT</td><td>Network service ports</td><td>443, 8443, 80</td></tr>
                                                    <tr><td className="font-bold underline">PARAMETER</td><td>URL or Request body parameters</td><td>user_id, token, id</td></tr>
                                                    <tr><td className="font-bold underline">HEADER</td><td>HTTP Request headers</td><td>Authorization, X-API-Key</td></tr>
                                                    <tr><td className="font-bold underline">FILE</td><td>File paths or upload endpoints</td><td>/static/docs/report.pdf</td></tr>
                                                    <tr><td className="font-bold underline">OTHER</td><td>Anything else (custom)</td><td>Custom identifier</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="type-badge tracking-widest uppercase bg-slate-600">CSV/JSON `http_method` Values</div>
                                        <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm max-w-md">
                                            <table className="guide-table">
                                                <thead>
                                                    <tr>
                                                        <th>`http_method` String</th>
                                                        <th>Applicability</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr><td className="font-black text-blue-600">GET</td><td>Fetch data or View page</td></tr>
                                                    <tr><td className="font-black text-green-600">POST</td><td>Submit data or Login</td></tr>
                                                    <tr><td className="font-black text-amber-600">PUT / PATCH</td><td>Update existing resource</td></tr>
                                                    <tr><td className="font-black text-red-600">DELETE</td><td>Remove resource</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                                <button
                                    onClick={() => setShowImportGuide(false)}
                                    className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-black transition-colors"
                                >
                                    Got it, close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default BulkImportModal;
